import { DeviceMethod } from '@/services/device-http-proxy/types'

/**
 * Common body for HTTP requests and responses
 */
export interface HttpBodyCommon {
  /**
   * The body payload
   */
  data: string

  /**
   * Indicates whether the data is base64-encoded (used for binary data).
   * If not provided, false is assumed.
   */
  isBase64Encoded?: boolean
}

/**
 * Body for HTTP requests
 */
export interface HttpRequestBody extends HttpBodyCommon {
  /**
   * Content type of the request body. Overrides any content-type header
   * supplied in a request.
   */
  contentType?: string
}

/**
 * Body for HTTP responses
 */
export interface HttpResponseBody extends HttpBodyCommon {
  /**
   * Content type of the response body. Must match the content-type header.
   */
  contentType: string
}

/**
 * Attributes that are common to HTTP requests and responses
 */
export interface HttpCommon {
  /**
   * Path part of the URL. The scheme and host are filled in downstream.
   */
  path: string

  /**
   * Identifier that is valid as long as the device connection for which
   * requests are being made is active
   */
  sessionId?: string
}

/**
 * Contains the data for an HTTP request sent to the device by the Device HTTP Proxy
 */
export interface HttpRequest extends HttpCommon {
  /**
   * HTTP request headers, if any are required in the request
   */
  headers?: Record<string, string>

  /**
   * The request timeout. Defaults to 60 seconds if not provided. Note that
   * this is the request timeout for a single request between the Device HTTP Proxy
   * and the device, and is not the timeout for the client method call. A single
   * request through the client can result in multiple requests between the Device HTTP Proxy
   * and the device for retries, as well as blocking user interaction if the allowUserInteraction
   * boolean is true.
   */
  timeout?: number

  /**
   * Indicates whether the native Device HTTP Proxy may display a blocking UI. Defaults to false if
   * not provided. If this value is true, the Device HTTP Proxy may display a UI for things
   * like entering credentials to acquire an authorization token for the request.
   */
  allowUserInteraction?: boolean

  /**
   * HTTP request method, uppercased (GET/POST/PUT/...). If not provided, 'GET' is assumed
   */
  method?: DeviceMethod

  /**
   * HTTP request body, if required
   */
  body?: HttpRequestBody
}

/**
 * Contains the data for an HTTP response for an HTTP request
 * sent by the native side
 */
export interface DeviceHttpProxyPluginHttpResponse extends HttpCommon {
  /**
   * HTTP request method, uppercased (GET/POST/PUT/...). If not provided, 'GET' is assumed
   */
  method: DeviceMethod

  /**
   * HTTP response headers
   */
  headers: Record<string, string>

  /**
   * HTTP response status code (including 4XX statuses). Will be 0 if no status code was returned
   */
  statusCode: number
  /**
   * HTTP response body, if the response has a body
   */
  body?: HttpResponseBody
}

/**
 * Enumerates errors that can occur during an HTTP request through the Device HTTP Proxy
 */
export enum DeviceHttpProxyPluginErrorType {
  /**
   * Indicates that an argument through the client method call was invalid
   */
  invalidArgument = 'invalidArgument',
  /**
   * Indicates that session identifier used in the request does not correspond to
   * any known session identifier
   */
  invalidSession = 'invalidSession',
  /**
   * Indicates that session identifier used in the request corresponds to a device session
   * that is no longer active
   */
  sessionNotActive = 'sessionNotActive',
  /**
   * Device is currently active but is offline
   */
  deviceOffline = 'deviceOffline',
  /**
   * Indicates that the HTTP request timed out
   */
  requestTimedOut = 'requestTimedOut',
  /**
   * Indicates that the HTTP request was cancelled at some point downstream
   */
  cancelled = 'cancelled',
  /**
   * Indicates an unexpected error has occurred
   */
  unknownError = 'unknownError',
  /**
   * Indicates that the dataValve component had an error filtering the HTTP response
   * from the printer
   */
  dataValveError = 'dataValveError',
}

/**
 * Represents an error that occurs when a request is sent using the Device
 * HTTP Proxy
 */
export interface DeviceHttpProxyPluginResponseError {
  /**
   * Session identifier passed in the request, if provided in the request. Note: the only time this
   * won't be provided is if a session identifier was not passed in the request, in which case the
   * errorType value must be DeviceHttpProxyErrorType.invalidArgument.
   */
  sessionId?: string

  /**
   * The error code
   */
  errorType: DeviceHttpProxyPluginErrorType

  /**
   * An optional message with more details about the error.
   */
  reason?: string
}

/**
 * Provides the interface for the Device HTTP Proxy, allowing HTTP requests to be sent
 * to the currently active device
 */
export interface DeviceHttpProxyClient {
  /**
   * Allows the caller to make an HTTP request to the device currently active
   * @param options Options to start a session
   * @returns A promise which resolves to the HTTP response or an error
   */
  sendRequest(
    options: HttpRequest,
  ): Promise<
    DeviceHttpProxyPluginHttpResponse | DeviceHttpProxyPluginResponseError
  >

  sendRequestWithCacheStrategy?: (options: {
    request: HttpRequest
    cacheStrategy: 'printerOnly' | 'cacheFirst'
    maxAgeInSeconds: number
  }) => Promise<
    DeviceHttpProxyPluginHttpResponse | DeviceHttpProxyPluginResponseError
  >
}
